/**
 * <sky-collection> / <sky-search> — collection and search page behaviour:
 * filter modal (with price slider), categories popover, and "Load more" pagination.
 * Filtering submits the form (GET), so it keeps working without JavaScript.
 */
class SkyCollection extends HTMLElement {
  connectedCallback() {
    this.filter = /** @type {HTMLElement | null} */ (this.querySelector('#SkyFilter'));
    this.cats = this.querySelector('[data-cats]');
    this.catsToggle = this.querySelector('[data-toggle-cats]');

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('input', (event) => this.onInput(event));
    this.addEventListener('submit', (event) => this.onSubmit(event));
    // Sort dropdown: pick an option -> submit (keeps active filters via hidden inputs)
    this.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (!input.matches('[data-sort] input[name="sort_by"]')) return;
      input.closest('[data-sort]')?.querySelectorAll('.sky-col__sort-item').forEach((item) =>
        item.classList.toggle('is-active', item.contains(input))
      );
      input.form?.submit();
    });

    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const sort = /** @type {HTMLDetailsElement | null} */ (this.querySelector('[data-sort][open]'));
      if (sort) sort.open = false;
      if (this.filter && !this.filter.hidden) this.closeFilter();
      this.toggleCats(false);
    };
    this.onDocumentClick = (event) => {
      const target = /** @type {Node} */ (event.target);
      const sort = /** @type {HTMLDetailsElement | null} */ (this.querySelector('[data-sort]'));
      if (sort?.open && !sort.contains(target)) sort.open = false;
      if (!this.cats?.classList.contains('is-open')) return;
      if (!this.cats.contains(target) && !this.catsToggle?.contains(target)) this.toggleCats(false);
    };
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-open-filter]')) return this.openFilter();
    if (target.closest('[data-close-filter]')) return this.closeFilter();
    if (target.closest('[data-toggle-cats]')) return this.toggleCats();

    const more = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-load-more]'));
    if (more) {
      event.preventDefault();
      this.loadMore(more);
    }
  }

  /* ---------- Filter modal ---------- */

  openFilter() {
    if (!this.filter) return;
    this.toggleCats(false);
    this.filter.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.filter?.classList.add('is-open');
        /** @type {HTMLElement | null} */ (this.filter?.querySelector('.sky-filter__box'))?.focus({ preventScroll: true });
      })
    );
  }

  closeFilter() {
    if (!this.filter) return;
    this.filter.classList.remove('is-open');
    setTimeout(() => {
      if (this.filter && !this.filter.classList.contains('is-open')) this.filter.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');
    }, 400);
    /** @type {HTMLElement | null} */ (this.querySelector('[data-open-filter]'))?.focus({ preventScroll: true });
  }

  /** Keep the double slider and the two price inputs in sync. */
  /** @param {Event} event */
  onInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const range = this.querySelector('[data-range]');
    if (!range) return;

    const sliderMin = /** @type {HTMLInputElement} */ (range.querySelector('[data-range-min]'));
    const sliderMax = /** @type {HTMLInputElement} */ (range.querySelector('[data-range-max]'));
    const fieldMin = /** @type {HTMLInputElement} */ (this.querySelector('[data-price-min]'));
    const fieldMax = /** @type {HTMLInputElement} */ (this.querySelector('[data-price-max]'));
    const max = Number(sliderMax.max) || 1;

    if (input === sliderMin || input === sliderMax) {
      if (Number(sliderMin.value) > Number(sliderMax.value)) {
        if (input === sliderMin) sliderMin.value = sliderMax.value;
        else sliderMax.value = sliderMin.value;
      }
      fieldMin.value = sliderMin.value === '0' ? '' : sliderMin.value;
      fieldMax.value = sliderMax.value === String(max) ? '' : sliderMax.value;
    } else if (input === fieldMin || input === fieldMax) {
      sliderMin.value = fieldMin.value || '0';
      sliderMax.value = fieldMax.value || String(max);
    } else {
      return;
    }

    range.setAttribute(
      'style',
      `--min: ${(Number(sliderMin.value) / max) * 100}%; --max: ${(Number(sliderMax.value) / max) * 100}%;`
    );
  }

  /** Drop empty fields so the URL stays clean. */
  /** @param {SubmitEvent} event */
  onSubmit(event) {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-filter-form]')) return;
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (!/** @type {HTMLInputElement} */ (input).value) input.removeAttribute('name');
    });
  }

  /* ---------- Categories ---------- */

  /** @param {boolean} [force] */
  toggleCats(force) {
    if (!this.cats || !this.catsToggle) return;
    const open = force ?? !this.cats.classList.contains('is-open');
    this.cats.classList.toggle('is-open', open);
    this.catsToggle.setAttribute('aria-expanded', String(open));
  }

  /* ---------- Load more ---------- */

  /** @param {HTMLAnchorElement} button */
  async loadMore(button) {
    const tag = this.localName;
    button.setAttribute('aria-busy', 'true');

    /** @param {Element} grid @param {Element[]} items */
    const append = (grid, items) =>
      items.forEach((item, i) => {
        item.classList.add('is-new');
        /** @type {HTMLElement} */ (item).style.setProperty('--i', String(i));
        grid.append(document.importNode(item, true));
      });

    try {
      const html = await (await fetch(button.href)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const groups = [...doc.querySelectorAll(`${tag} [data-group]`)];

      if (groups.length) {
        // search: results are grouped by type; a group missing on this page is added whole
        groups.forEach((group) => {
          const local = this.querySelector(`[data-group="${group.getAttribute('data-group')}"] [data-grid]`);
          if (local) append(local, [...group.querySelectorAll('[data-grid] > li')]);
          else this.querySelector('[data-groups]')?.append(document.importNode(group, true));
        });
      } else {
        const grid = this.querySelector('[data-grid]');
        if (grid) append(grid, [...doc.querySelectorAll(`${tag} [data-grid] > li`)]);
      }

      const total = Number(this.querySelector('[data-total]')?.getAttribute('data-total')) || 0;
      const viewed = [...this.querySelectorAll('[data-grid]')].reduce((sum, grid) => sum + grid.children.length, 0);
      const template = this.querySelector('[data-viewed-template]')?.innerHTML.trim() || '';
      const text = this.querySelector('[data-viewed-text]');
      if (text) text.textContent = template.replace('[viewed]', String(viewed)).replace('[total]', String(total));
      const progress = /** @type {HTMLElement | null} */ (this.querySelector('[data-progress]'));
      if (progress && total) progress.style.width = `${Math.min(100, (viewed / total) * 100)}%`;

      const next = /** @type {HTMLAnchorElement | null} */ (doc.querySelector(`${tag} [data-load-more]`));
      if (next) button.href = next.href;
      else button.remove();
    } catch {
      window.location.href = button.href;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('sky-collection')) customElements.define('sky-collection', SkyCollection);
if (!customElements.get('sky-search')) customElements.define('sky-search', class extends SkyCollection {});
